/**
 * Shared presentation for the notification surfaces — the severity icon,
 * the screen-reader severity prefix, and the accent-colour class.
 *
 * Split out of :file:`toast.tsx` / :file:`banner-host.tsx` so both draw the
 * severity vocabulary from one place (CLAUDE.md §4.4 — no duplicated
 * logic), and so the icon/label mapping is unit-testable without rendering
 * a host.
 *
 * # Colour is never the sole differentiator
 *
 * Every severity carries three redundant signals: an **icon**, a
 * **screen-reader word prefix** ("Warning: …"), and a colour. Flow F
 * §Accessibility requires the first two precisely so the surface survives
 * both monochrome rendering and colour-vision deficiency (REQ-UI-046).
 *
 * # Which tokens
 *
 * Accent colours are the Appaloosa **semantic feedback layer** —
 * ``--info`` / ``--positive`` / ``--warning`` / ``--destructive`` — per
 * ``chrome-spec.md`` §"Toast layer (production)": *"Leading-icon color by
 * toast type: info ``--info``, success ``--positive``, warning
 * ``--warning``, error / critical ``--destructive``. Replaces the original
 * status-tint background mapping."* Note the deliberate divergence from
 * the ``--status-*`` map palette the Alerts-panel *badges* use: a toast is
 * chrome feedback, not a map-entity status.
 *
 * Cross-references:
 *   - ``storyboards/shared/chrome-spec.md`` §"Toast layer (production)".
 *   - ``storyboards/shared/design-system.md`` §"Semantic feedback layer".
 *   - storyboard Flow F §Accessibility; REQ-UI-046.
 */
import {
  CheckCircle,
  Info,
  Warning,
  WarningCircle,
  WarningOctagon,
  type Icon,
} from "@phosphor-icons/react";
import type { NotificationSeverity } from "@/notifications/notification-types";

/** Presentation vocabulary for one severity. */
export interface SeverityChrome {
  /** Phosphor icon component rendered leading the title (``aria-hidden``). */
  readonly Glyph: Icon;
  /**
   * Word prefix read to assistive tech before the title, so the severity
   * is spoken and not merely coloured (Flow F §Accessibility).
   */
  readonly srPrefix: string;
  /** Tailwind text-colour class for the icon (semantic feedback layer). */
  readonly accentClass: string;
}

const _CHROME_BY_SEVERITY: Readonly<Record<NotificationSeverity, SeverityChrome>> = {
  info: { Glyph: Info, srPrefix: "Information", accentClass: "text-info" },
  success: { Glyph: CheckCircle, srPrefix: "Success", accentClass: "text-positive" },
  warning: { Glyph: Warning, srPrefix: "Warning", accentClass: "text-warning" },
  error: { Glyph: WarningCircle, srPrefix: "Error", accentClass: "text-destructive" },
  critical: { Glyph: WarningOctagon, srPrefix: "Critical", accentClass: "text-destructive" },
};

/**
 * The icon, screen-reader prefix, and accent class for a severity. Total —
 * every :type:`NotificationSeverity` has an entry, so this never returns
 * ``undefined`` and callers need no fallback.
 */
export function severityChrome(severity: NotificationSeverity): SeverityChrome {
  return _CHROME_BY_SEVERITY[severity];
}
